import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import { setToolboxCreatingVideo } from 'js/src/data/app/app-actions';
import { setFocusedAssetInfo } from 'js/src/data/scenes/scenes-actions';

export interface HtmlDesignTarget {
    sceneId: string;
    assetId: string;
}

/**
 * A point in the scene where the graphic advances: something new arrives, what is already on screen
 * rearranges to make room for it, and often something leaves. `at` is seconds from the scene's
 * start — the unit narration words arrive in and tweens are positioned in.
 */
export interface ScenePhase {
    at: number;
    beat: string;
    /** Absent for a phase that only rearranges or removes what is already on screen. */
    enters?: string;
    transforms?: string;
    exits?: string;
}

/**
 * What the graphic contains, and when it advances — but not how it moves. The phase schedule commits
 * the agent to *when* the graphic changes and *what* changes, which is a pacing decision it can make
 * against the narration before any technique has been read; how each of those changes looks is the
 * technique's business, and is still chosen afterwards.
 */
export interface ScenePlan {
    text: string[];
    graphics: string[];
    layout: string;
    /**
     * When the graphic advances, in order. Always at least one entry — the graphic arriving — and
     * more as the scene lengthens, so the motion is staged across the scene instead of resolving
     * early and leaving a stretch that invites filler.
     */
    phases: ScenePhase[];
    /**
     * The figures the graphic plots, declared before it is drawn. Absent for the many graphics that
     * plot nothing; present, it is what the composition's own `data-l5-values` are checked against.
     */
    data?: { label: string; values: number[] }[];
}

/**
 * What every graphic in this video is built from: one look, authored once, injected into each
 * composition and enforced by lint. Video-level rather than per scene, which is what makes it the
 * video's continuity mechanism.
 */
export interface DesignSystem {
    look: string;
    css: string;
}

/**
 * `activeConversationTarget` is the motion graphics scene currently in edit mode: the scene others
 * dim around, that shows the composition scrubber and its selectable parts.
 */
export interface HtmlDesignState {
    activeConversationTarget: HtmlDesignTarget | null;
    planBySceneId: Record<string, ScenePlan>;
    /** The slice is per open video, so one system needs no keying. */
    designSystem: DesignSystem | null;
    /** Read when assigning the next scene, so a video varies its techniques. */
    techniquesBySceneId: Record<string, string[]>;
}

const initialState: HtmlDesignState = {
    activeConversationTarget: null,
    planBySceneId: {},
    designSystem: null,
    techniquesBySceneId: {},
};

const htmlDesignSlice = createSlice({
    name: 'htmlDesign',
    initialState,
    reducers: {
        setActiveHtmlDesign(state, action: PayloadAction<HtmlDesignTarget>) {
            state.activeConversationTarget = action.payload;
        },
        clearActiveHtmlDesign(state) {
            state.activeConversationTarget = null;
        },
        setHtmlDesignSystem(state, action: PayloadAction<DesignSystem>) {
            state.designSystem = action.payload;
        },
        setHtmlDesignPlan(
            state,
            action: PayloadAction<{ sceneId: string; plan: ScenePlan; techniques: string[] }>,
        ) {
            state.planBySceneId[action.payload.sceneId] = action.payload.plan;
            state.techniquesBySceneId[action.payload.sceneId] = action.payload.techniques;
        },
    },
    extraReducers: (builder) => {
        // Focusing any luminary asset — a caption, the watermark, a sticker — means the user has moved
        // on to editing that asset, so a codeo scene in edit mode leaves it rather than leaving two
        // editing surfaces open at once.
        builder.addCase(setFocusedAssetInfo, (state) => {
            state.activeConversationTarget = null;
        });
        // Everything here describes one video, and nothing else clears it: the slice outlives a
        // build in the same SPA session, so the next one would plan against the last one's scenes.
        // Keyed on a build starting rather than on the video id, which is still null while the
        // first scenes are authored and only lands at turn end.
        builder.addCase(setToolboxCreatingVideo, (state, action) => {
            if (!action.payload) {
                return;
            }
            state.planBySceneId = {};
            state.techniquesBySceneId = {};
            state.designSystem = null;
        });
    },
});

export const {
    setActiveHtmlDesign,
    clearActiveHtmlDesign,
    setHtmlDesignPlan,
    setHtmlDesignSystem,
} = htmlDesignSlice.actions;
export const htmlDesignReducer = htmlDesignSlice.reducer;
